/**
 * GPS and distance calculation utilities
 */

/**
 * Calculate distance between two GPS points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point  
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371 // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Calculate total distance from GPS tracking points
 * @param {Array} tracks - GPS tracking points
 * @returns {number} Total distance in kilometers
 */
export function calculateTotalDistance(tracks) {
  let totalDistance = 0
  
  for (let i = 1; i < tracks.length; i++) {
    const dist = haversineDistance(
      tracks[i - 1].latitude,
      tracks[i - 1].longitude,
      tracks[i].latitude,
      tracks[i].longitude
    )
    totalDistance += dist
  }
  
  return totalDistance
}

/**
 * Calculate elevation gain from GPS tracking points
 * @param {Array} tracks - GPS tracking points with altitude
 * @returns {number} Elevation gain in meters
 */
export function calculateElevationGain(tracks) {
  let gain = 0
  let previousAltitude = tracks[0]?.altitude || 0
  
  for (let i = 1; i < tracks.length; i++) {
    const currentAltitude = tracks[i].altitude || 0
    const change = currentAltitude - previousAltitude
    if (change > 0) {
      gain += change
    }
    previousAltitude = currentAltitude
  }
  
  return Math.round(gain)
}

/**
 * Check if track data contains elevation information
 * @param {Array} tracks - Array of track points
 * @returns {boolean} True if elevation data is available
 */
export function hasElevationData(tracks) {
  if (!Array.isArray(tracks) || tracks.length === 0) {
    return false
  }
  
  // Check if at least some points have altitude data
  return tracks.some(track => 
    track.altitude !== undefined && 
    track.altitude !== null && 
    !isNaN(track.altitude)
  )
}

/**
 * Find the track point with the most recent timestamp
 * @param {Array} tracks - Array of track points
 * @returns {Object|null} Track point with latest time or null if none found
 */
export function findLatestPositionByTime(tracks) {
  if (!Array.isArray(tracks) || tracks.length === 0) {
    return null
  }
  
  // Filter out tracks without valid time data
  const tracksWithTime = tracks.filter(track => 
    track.time && !isNaN(new Date(track.time).getTime())
  )
  
  if (tracksWithTime.length === 0) {
    // Fallback to last track if no time data available
    return tracks[tracks.length - 1] || null
  }
  
  // Find track with the most recent timestamp
  return tracksWithTime.reduce((latest, current) => {
    const latestTime = new Date(latest.time)
    const currentTime = new Date(current.time)
    return currentTime > latestTime ? current : latest
  })
}

/**
 * Convert degrees to radians
 * @param {number} degrees 
 * @returns {number} Radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180)
}